import React from 'react'
import SuspenseLoading from '@components/SuspenseLoading'
import Divider from '@components/V3/Divider'
import classnames from '@helpers/misc/classnames'
import formatNumber from '@i18n/formatNumber'
import {useTranslation} from 'next-i18next'

import Invoice from './Invoice'
import Item from './Item'
import Transaction from './Transaction'
import useOrderDetails from './useOrderDetails.main'

const SummaryItem = ({
  label,
  total,
  discount,
  id
}: {
  label: string
  total: number
  discount?: boolean
  id?: string
}) => (
  <div className="flex mb-3">
    <div className="flex-1 font-semibold">{label}</div>
    <div id={id} className={discount ? 'text-green-500' : ''}>
      {discount ? ' - ' : ''}
      {formatNumber(total, 'money')}
    </div>
  </div>
)

const Discounts = () => {
  const orderDetails = useOrderDetails()
  const {t} = useTranslation('tracking', {keyPrefix: 'order.inner'})

  const totalDiscount = orderDetails.baseTotalPrice - orderDetails.totalPrice

  if (!totalDiscount) {
    return null
  }

  const itemizedDiscount = [
    orderDetails.loyaltyTransaction?.amount,
    orderDetails.overchargeDiscount,
    orderDetails.couponDiscount
  ].reduce((acc, cur) => acc + (cur || 0), 0)

  return (
    <>
      {orderDetails.loyaltyTransaction?.amount ? (
        <SummaryItem
          id={'order-loyalty-discount'}
          discount
          label={t('loyaltyDiscount', {loyaltyName: orderDetails.loyaltyTransaction.loyaltyName})}
          total={orderDetails.loyaltyTransaction.amount}
        />
      ) : null}
      {orderDetails.overchargeDiscount ? (
        <SummaryItem
          id={'order-overcharge-discount'}
          discount
          label={t('overchargeDiscount')}
          total={orderDetails.overchargeDiscount}
        />
      ) : null}
      {orderDetails.couponDiscount ? (
        <SummaryItem
          id={'order-coupon-discount'}
          discount
          label={t('couponDiscount', {couponName: orderDetails.couponName})}
          total={orderDetails.couponDiscount}
        />
      ) : null}

      {totalDiscount === itemizedDiscount ? null : (
        <SummaryItem
          id={'order-total-discount'}
          discount
          label={t('totalDiscount')}
          total={totalDiscount}
        />
      )}
    </>
  )
}

function DetailsInner(props: DetailsProps) {
  const orderDetails = useOrderDetails()
  const {t} = useTranslation('tracking', {keyPrefix: 'order.inner'})

  return (
    <div className={classnames('p-4 flex flex-col text-sm', {hidden: !props.showDetails})}>
      <Transaction />
      <div>
        <h5 className="font-bold mb-2">{t('cartDetails')}</h5>
        {orderDetails.items.map(item => (
          <Item key={item._id} item={item} />
        ))}
      </div>
      <Divider className="mb-4" />

      <SummaryItem
        id={'order-subtotal'}
        label={t('subtotal')}
        total={orderDetails.baseItemsPrice}
      />

      <Discounts />

      {orderDetails.bagAmount ? (
        <SummaryItem id={'order-bag-fee'} label={t('bagAmount')} total={orderDetails.bagAmount} />
      ) : null}

      {orderDetails.deliveryType === 'delivery' ? (
        <SummaryItem
          id={'order-delivery-fee'}
          label={t('delivery')}
          total={orderDetails.deliveryFee}
        />
      ) : null}

      <SummaryItem id={'order-tip'} label={t('tip')} total={orderDetails.tipAmount} />

      <div className="text-base font-semibold">
        <SummaryItem id={'order-amount-to-pay'} label={t('total')} total={orderDetails.amountToPay} />
      </div>
      <Invoice />
    </div>
  )
}

interface DetailsProps {
  showDetails: boolean
}

export default function Details(props: DetailsProps) {
  return (
    <SuspenseLoading>
      <DetailsInner {...props} />
    </SuspenseLoading>
  )
}
